import React from 'react'

interface Cliente {
  cliente_id: string
  nome_empresa: string
  segmento_nome: string
  status_cliente: string
  tipo_oportunidade: string | null
  prioridade_real: number
  roi: number | null
  faturamento: number | null
  potencial_receita: number | null
  acao_recomendada: string
}

interface BlocoPrioridadesProps {
  riscoAlto: Cliente[]
  riscoMedio: Cliente[]
  oportunidades: Cliente[]
}

const BlocoPrioridades: React.FC<BlocoPrioridadesProps> = ({ 
  riscoAlto, 
  riscoMedio, 
  oportunidades 
}) => {
  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0
    }).format(value)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'risco_alto': return 'bg-red-100 text-red-800 border-red-300'
      case 'risco_medio': return 'bg-orange-100 text-orange-800 border-orange-300'
      case 'oportunidade': return 'bg-green-100 text-green-800 border-green-300'
      default: return 'bg-gray-100 text-gray-800 border-gray-300'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'risco_alto': return '🚨'
      case 'risco_medio': return '⚠️'
      case 'oportunidade': return '🎯'
      default: return '📊'
    }
  }

  const getTipoOportunidadeLabel = (tipo: string | null) => {
    if (!tipo) return ''
    const labels: Record<string, string> = {
      'escala': '📈 Escala',
      'conversao': '🎯 Conversão',
      'maturidade': '📚 Maturidade',
      'estrutura': '🏗️ Estrutura'
    }
    return labels[tipo] || tipo
  }

  const ClienteCard: React.FC<{ cliente: Cliente; tipo: 'risco' | 'oportunidade' }> = ({ cliente, tipo }) => (
    <div className={`p-3 rounded-lg border-2 ${getStatusColor(cliente.status_cliente)} hover:shadow-md transition-shadow`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-lg">{getStatusIcon(cliente.status_cliente)}</span>
            <h4 className="font-semibold text-sm">{cliente.nome_empresa}</h4>
          </div>
          <p className="text-xs text-gray-600">{cliente.segmento_nome || 'Sem segmento'}</p>
        </div>
        <div className="text-right">
          <div className="text-xs font-medium text-gray-700">
            Prioridade: {cliente.prioridade_real}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-2 text-xs">
        <div>
          <span className="text-gray-600">Faturamento:</span>
          <div className="font-semibold">{formatCurrency(cliente.faturamento)}</div>
        </div>
        <div>
          <span className="text-gray-600">ROI:</span>
          <div className="font-semibold">
            {cliente.roi !== null ? `${cliente.roi.toFixed(0)}%` : 'N/A'}
          </div>
        </div>
      </div>

      {tipo === 'oportunidade' && cliente.tipo_oportunidade && (
        <div className="mb-2">
          <span className="inline-block px-2 py-1 text-xs font-medium bg-white rounded border">
            {getTipoOportunidadeLabel(cliente.tipo_oportunidade)}
          </span>
          {cliente.potencial_receita !== null && cliente.potencial_receita > 0 && (
            <span className="ml-2 text-xs text-gray-600">
              Potencial: {formatCurrency(cliente.potencial_receita)}
            </span>
          )}
        </div>
      )}

      <div className="pt-2 border-t border-gray-200">
        <div className="text-xs text-gray-700">
          <span className="font-medium">Ação:</span> {cliente.acao_recomendada}
        </div>
      </div>
    </div>
  )

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-3xl">🚨</span>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Prioridades do Mês</h2>
          <p className="text-sm text-gray-600">Clientes que precisam de atenção imediata</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Risco Alto */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🚨</span>
            <h3 className="text-lg font-bold text-red-700">
              Risco Alto ({riscoAlto.length})
            </h3>
          </div>
          <div className="space-y-3">
            {riscoAlto.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                <div className="text-4xl mb-2">✅</div>
                Nenhum cliente em risco alto
              </div>
            ) : (
              riscoAlto.slice(0, 5).map(cliente => (
                <ClienteCard key={cliente.cliente_id} cliente={cliente} tipo="risco" />
              ))
            )}
            {riscoAlto.length > 5 && (
              <div className="text-center text-sm text-gray-600 pt-2">
                + {riscoAlto.length - 5} clientes
              </div>
            )}
          </div>
        </div>

        {/* Risco Médio */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">⚠️</span>
            <h3 className="text-lg font-bold text-orange-700">
              Risco Médio ({riscoMedio.length})
            </h3>
          </div>
          <div className="space-y-3">
            {riscoMedio.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                <div className="text-4xl mb-2">✅</div>
                Nenhum cliente em risco médio
              </div>
            ) : (
              riscoMedio.slice(0, 5).map(cliente => (
                <ClienteCard key={cliente.cliente_id} cliente={cliente} tipo="risco" />
              ))
            )}
            {riscoMedio.length > 5 && (
              <div className="text-center text-sm text-gray-600 pt-2">
                + {riscoMedio.length - 5} clientes
              </div>
            )}
          </div>
        </div>

        {/* Oportunidades */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🎯</span>
            <h3 className="text-lg font-bold text-green-700">
              Oportunidades ({oportunidades.length})
            </h3>
          </div>
          <div className="space-y-3">
            {oportunidades.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                <div className="text-4xl mb-2">🔍</div>
                Nenhuma oportunidade identificada
              </div>
            ) : (
              oportunidades.slice(0, 5).map(cliente => (
                <ClienteCard key={cliente.cliente_id} cliente={cliente} tipo="oportunidade" />
              ))
            )}
            {oportunidades.length > 5 && (
              <div className="text-center text-sm text-gray-600 pt-2">
                + {oportunidades.length - 5} oportunidades
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BlocoPrioridades
